/**
 * Shared utility functions for tour/artist scoring algorithms
 */

/**
 * Get weekly rotation seed - changes every Monday at midnight UTC
 * Returns a deterministic seed value that's stable for a full week
 */
export const getWeeklySeed = (): number => {
  const now = new Date();
  // Week number based on Unix epoch
  const weekNumber = Math.floor(now.getTime() / (7 * 24 * 60 * 60 * 1000));
  return weekNumber;
};

/**
 * Seeded pseudo-random number generator (Mulberry32)
 * Produces deterministic random numbers given a seed
 */
export const seededRandom = (seed: number): (() => number) => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Shuffle array using seeded random for weekly rotation
 */
export const seededShuffle = <T>(array: T[], seed: number): T[] => {
  const random = seededRandom(seed);
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * Festival detection patterns
 */
const FESTIVAL_PATTERNS = /festival|fest\s|2-day|3-day|4-day|two-day|three-day|all\s*points|ticket\s*\+\s*hotel|abono|weekend\s*pass|ga\s*pass|vip\s*pass|camping\s*pass|day\s*pass|\bday\s*\d\b/i;

/**
 * Seat package / venue section patterns - these are ticket types, not real tours
 * Examples: "Mezzanine 21+", "Doja Cat Upstairs Section", "(21+ Event)", "16 and Over - Monday"
 * Also catches ticket package patterns like "Venue Premium", "VIP Package", "Meet & Greet"
 */
const SEAT_PACKAGE_PATTERNS = new RegExp([
  // Venue sections
  '\\bmezzanine\\b', '\\bbalcony\\b', '\\bupstairs\\b', '\\bdownstairs\\b',
  '\\bsection\\b', '\\bseating\\b', '\\bga\\s*floor\\b', '\\bpit\\b', '\\bvip\\s*table',
  
  // Age restrictions
  '\\b21\\+', '\\b18\\+', '\\b16\\+', '\\(\\d+\\+\\s*event\\)',
  '\\d+\\s*and\\s*(over|under)\\b',
  
  // Day-based packages
  '\\bmonday\\b.*-\\s*\\w+$', '\\btuesday\\b.*-\\s*\\w+$', '\\bwednesday\\b.*-\\s*\\w+$',
  '\\bthursday\\b.*-\\s*\\w+$', '\\bfriday\\b.*-\\s*\\w+$', '\\bsaturday\\b.*-\\s*\\w+$',
  '\\bsunday\\b.*-\\s*\\w+$',
  
  // Age-only entries
  '^\\(\\d+\\+\\s*event\\)$',
  
  // Package prefixes/names
  '\\bvenue\\s*premium\\b', '\\bclub\\s*level\\b', '\\bpackage\\b',
  '\\bmeet\\s*&\\s*greet\\b', '\\bvinyl\\s*room\\b', '\\bplatinum\\s*experience\\b',
  '\\bpremium\\s*seating\\b', '\\bpremium\\s*club\\s*seats?\\b', '\\bclub\\s*seats?\\b',
  '\\bearly\\s*entry\\b', '\\bsoundcheck\\b',
  '\\bgold\\s*circle\\b', '\\bfront\\s*row\\b', '\\bplatinum\\s*seats?\\b',
  
  // Ticket packages
  '\\bticket\\s*\\+\\s*hotel\\b', '\\bofficial\\s*ticket\\b',
  '\\bbundles?\\b', '\\bticket.*hotel\\b', '\\bhotel.*ticket\\b',
  
  // Seat types
  '\\bcomfort\\s*seats?\\b', '\\bbox\\s*seat\\b', '\\breserved\\s*seat\\b',
  '\\bbusiness\\s*seat\\b', '\\b\\d+-day\\s*ticket\\b',
  
  // Venue areas
  '\\bfunction\\s*room\\b', '\\borangery\\b', '\\blounge\\b', '\\bterrace\\b',
  '\\bgarden\\s*area\\b', '\\bplaza\\s*de\\b',
  
  // Upgrade/add-on indicators
  '\\bupgrade\\b', '\\bniet\\s*inbegrepen\\b', '\\bnot\\s*included\\b',
  
  // Leading pipe character (package indicator)
  '^\\|',
  
  // Guest-only descriptors (not real tours)
  '^(w\\/|with|feat\\.?|featuring)\\s+[A-Z]',
  '^\\+\\s+[A-Z]',
  '^special\\s+guest',
  '^support(ed)?\\s+by',
  '^opening\\s+act'
].join('|'), 'i');

/**
 * Check if a tour name looks like a festival
 */
export const isFestivalTour = (tourName: string): boolean => {
  return FESTIVAL_PATTERNS.test(tourName || '');
};

/**
 * Check if a tour name looks like a seat package or venue section
 */
export const isSeatPackage = (tourName: string): boolean => {
  return SEAT_PACKAGE_PATTERNS.test(tourName || '');
};

/**
 * Calculate freshness bonus based on tour creation date
 * Tours created recently get a bonus to ensure visibility
 */
export const getFreshnessBonus = (createdAt: string | null | undefined): number => {
  if (!createdAt) return 0;
  
  const created = new Date(createdAt);
  const now = new Date();
  const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysSinceCreation <= 7) return 150;   // First week: big boost
  if (daysSinceCreation <= 14) return 75;   // Second week: moderate boost
  if (daysSinceCreation <= 30) return 25;   // First month: small boost
  return 0;
};

/**
 * Apply max-per-artist limit to a list of items
 * Returns filtered list where no artist appears more than maxPerArtist times
 */
export const limitPerArtist = <T extends { artistId?: string }>(
  items: T[],
  maxPerArtist: number
): T[] => {
  const artistCounts = new Map<string, number>();
  return items.filter((item) => {
    const artistId = item.artistId || 'unknown';
    const count = artistCounts.get(artistId) || 0;
    if (count >= maxPerArtist) return false;
    artistCounts.set(artistId, count + 1);
    return true;
  });
};
